<script setup lang="ts">
import type { ResolvePromise } from '@sortera/ee-utils'
import { destructurePromise } from '@sortera/ee-utils'
import { computed, ref, useTemplateRef, watch } from 'vue'

import { UiIcon } from '@/components/ui/UiIcon/index.ts'
import { useVModel } from '@/utils/use-vue/index.ts'

export type AutofocusElements = 'firstButton' | 'noFocus' | 'default'

interface UiDialogProps {
  isOpen: boolean
  autofocusElement?: AutofocusElements
  /** Can position dialog at the top/bottom on mobile if wanted. On desktop it is always centered. */
  mobilePosition?: Position
  title?: string | null
  fullHeight?: boolean
  showHeaderWithCloseBtn?: boolean
}

export type UiDialogEmits = {
  'update:isOpen': [value: boolean]
  /** Emits after dialog closing animation is fully done */
  didClose: []
  clickOutside: []
}

interface Slots {
  default(slotProps: SlotProps): unknown
}

type Position = 'center' | 'top' | 'bottom'

interface SlotProps {
  closeAndWaitTillAnimated: () => Promise<void>
}

const props = withDefaults(defineProps<UiDialogProps>(), {
  autofocusElement: 'default',
  mobilePosition: 'center',
  title: null,
  fullHeight: false,
  showHeaderWithCloseBtn: true,
})

const emit = defineEmits<UiDialogEmits>()
defineSlots<Slots>()
const slotProps = computed((): SlotProps => ({ closeAndWaitTillAnimated }))

defineOptions({
  inheritAttrs: false,
})

const CLASS_BY_POSITION: Record<Position, string> = {
  center: 'modal-middle',
  top: 'modal-top md:modal-middle',
  bottom: 'modal-bottom md:modal-middle',
}

const modalRef = useTemplateRef('modal')

// We need to remove the dialog from the DOM after it's closed.
// Otherwise if there are multiple dialogs on the page, strange bugs can happen,
// like changes done in one leaking into another.
const shouldRender = ref(props.isOpen)

const isOpenModel = useVModel(props, 'isOpen', emit)

watch(
  () => [isOpenModel.value, modalRef.value],
  () => {
    // needs to come first so that modalRef is set
    if (isOpenModel.value) shouldRender.value = true
    if (!modalRef.value) return
    if (isOpenModel.value) {
      shouldRender.value = true
      modalRef.value.showModal()
      destructuredClosingAnimationPromise = makeDestructuredPromise() // reset promise

      if (props.autofocusElement === 'firstButton') {
        const firstButton = modalRef.value?.querySelector('button')
        firstButton?.focus()
      }
    } else {
      modalRef.value?.close()
    }
  },
  { immediate: true, deep: true }
)

let destructuredClosingAnimationPromise = makeDestructuredPromise()

function makeDestructuredPromise(): [Promise<void>, ResolvePromise<void>] {
  const [promise, resolve] = destructurePromise()

  // eslint-disable-next-line promises/prefer-await-to-then
  promise.then(() => {
    shouldRender.value = false
  })
  return [promise, resolve]
}

function onTransitionEnd(event: Event): void {
  if (event.target !== modalRef.value) return // Ignore events from children
  if (isOpenModel.value) return // Ignore events when opening
  emit('didClose')

  const [, resolve] = destructuredClosingAnimationPromise
  resolve()
}

/** Meant to be used in scoped slot. Will wait for closing animation to finish, if there is any. */
function closeAndWaitTillAnimated(): Promise<void> {
  isOpenModel.value = false
  const [promise] = destructuredClosingAnimationPromise
  return promise
}

function onClickOutside(): void {
  emit('clickOutside')
}

function onClickClose(): void {
  isOpenModel.value = false
}
</script>

<template>
  <Teleport to="body">
    <dialog
      v-if="shouldRender"
      v-bind="$attrs"
      ref="modal"
      class="modal"
      :class="[CLASS_BY_POSITION[props.mobilePosition]]"
      @cancel.prevent="isOpenModel = false /** Circumvent default closing of dialog when ESC is clicked */"
      @transitionend="onTransitionEnd"
    >
      <div
        class="modal-backdrop"
        @click.prevent="onClickOutside"
      />

      <div
        class="modal-box"
        :class="{ 'h-full': props.fullHeight, 'p-0': !props.showHeaderWithCloseBtn }"
      >
        <!-- hidden focus trap. Can not have `display: none`, or it won't get focus in hosted environments -->
        <button
          v-if="props.autofocusElement === 'noFocus'"
          autofocus
          class="fixed -top-10 -left-10 h-0 w-0 opacity-0"
        />

        <div class="flex h-full flex-col gap-4">
          <header
            v-if="props.showHeaderWithCloseBtn"
            class="flex items-center justify-between"
          >
            <h3 v-if="props.title">
              {{ props.title }}
            </h3>
            <!-- empty div for layout only -->
            <div v-else />

            <form method="dialog">
              <button
                class="btn btn-circle btn-ghost btn-sm"
                @click="onClickClose"
              >
                <UiIcon
                  class="h-5"
                  name="close"
                />
              </button>
            </form>
          </header>

          <slot v-bind="slotProps" />
        </div>
      </div>
    </dialog>
  </Teleport>
</template>
